<script>
  import { createEventDispatcher, onDestroy } from "svelte";

  const dispatch = createEventDispatcher();

  export let open = false;
  export let modalHeading = "";
  export let width = 500;
  export let height = null;
  export let fullscreen = false;
  export let preventCloseOnClickOutside = true;

  $: finalWidth = typeof width === "number" ? `${width}px` : width;
  $: finalHeight = height
    ? typeof height === "number"
      ? `${height}px`
      : height
    : "auto";

  function close() {
    open = false;
    dispatch("close");
  }

  function handleBackdropClick(e) {
    if (!preventCloseOnClickOutside && e.target === e.currentTarget) {
      close();
    }
  }

  // Prevent body scroll when dialog is open
  $: if (typeof document !== "undefined") {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }

  onDestroy(() => {
    if (typeof document !== "undefined") {
      document.body.style.overflow = "";
    }
  });
</script>

{#if open}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div
    class="dialog-backdrop"
    on:click={handleBackdropClick}
    role="presentation"
  >
    <div
      class="dialog-container {fullscreen ? 'is-fullscreen' : ''}"
      style="--dialog-width: {finalWidth}; --dialog-height: {finalHeight};"
      role="dialog"
      aria-modal="true"
      aria-label={modalHeading}
    >
      <header class="dialog-header">
        {#if modalHeading}
        <h3 class="dialog-heading">{modalHeading}</h3>
        {/if}
        <slot name="heading"/>
        <button class="close-button" on:click={close} aria-label="Close">
          <svg
            focusable="false"
            preserveAspectRatio="xMidYMid meet"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            width="20"
            height="20"
            viewBox="0 0 32 32"
            aria-hidden="true"
            class="bx--modal-close__icon"
          >
            <path
              d="M24 9.4L22.6 8 16 14.6 9.4 8 8 9.4 14.6 16 8 22.6 9.4 24 16 17.4 22.6 24 24 22.6 17.4 16 24 9.4z"
            ></path>
          </svg>
        </button>
      </header>
      <div class="divider"></div>
      <div class="dialog-content">
        <slot />
      </div>
    </div>
  </div>
{/if}

<style>
  .dialog-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(22, 22, 22, 0.5); /* Carbon Overlay Color */
    z-index: 9000;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 1;
    transition: opacity 0.2s;
  }

  .dialog-container {
    background-color: white;
    width: var(--dialog-width);
    height: var(--dialog-height);
    max-height: 95vh;
    max-width: 95vw;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);
    border-radius: 0.5rem;
    padding: 0.5rem;
    position: relative;
    transition: all 0.24s cubic-bezier(0, 0, 0.38, 0.9);
  }

  /* --- Fullscreen Override --- */
  .dialog-container.is-fullscreen {
    width: 100vw !important;
    height: 100vh !important;
    max-width: none !important;
    max-height: none !important;
    border-radius: 0;

    margin: 0;
    top: 0;
    left: 0;
    padding: 0 !important;
  }

  .dialog-container.is-fullscreen .dialog-content {
    height: 100%;
    padding: 1rem;
  }

  .dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 0.5rem 0.5rem 1rem;
    min-height: 3rem;
  }

  .dialog-heading {
    margin: 0;
    padding: 0;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: 0.01rem;
    color: #161616;
    flex: 1;
    padding-right: 2rem;
  }

  .close-button {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    width: 3rem;
    height: 3rem;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: background-color 0.11s;
    outline: none;
    color: #161616;
  }

  .close-button:hover {
    background-color: #e5e5e5;
  }

  .close-button:focus {
    outline: 2px solid #0f62fe; /* Carbon Blue focus */
    outline-offset: -2px;
  }

  .divider {
    width: 100%;
    height: 1px;
    background: #d9d9d9;
    flex-shrink: 0;
    margin-bottom: 0;
  }

  .dialog-content {
    padding: 1rem;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
  }
</style>
